import { Symbology } from '@scandit/web-datacapture-barcode'
import type { DataCaptureView } from '@scandit/web-datacapture-core'
import {
  CustomBarcodeBuilder,
  ImeiOneBarcodeBuilder,
  type LabelCapture,
  LabelCaptureSettings,
  LabelDefinitionBuilder,
  SerialNumberBarcodeBuilder,
} from '@scandit/web-datacapture-label'
import type { LabelPreset } from '../LabelCaptureSettingsStore'
import type { BuildSettingsContext, ControllerDeps, PresetController, Teardown } from './PresetController'
import { activateValidationFlow } from './ValidationFlowController'

export class SmartphoneBoxController implements PresetController {
  readonly preset: LabelPreset = 'smartphoneBox'

  constructor(private readonly deps: ControllerDeps) {}

  async buildSettings(ctx: BuildSettingsContext): Promise<LabelCaptureSettings> {
    const labelDefinition = await new LabelDefinitionBuilder()
      .addCustomBarcode(
        // UPC is restricted to EAN13UPCA only. If we allowed Code128 here,
        // UPC (no anchor regex) would greedily claim the IMEI/Serial Code128
        // barcodes and the dedicated IMEI1/Serial slots would never fill,
        // leaving the label permanently unvalidated. Mirrors iOS canonical.
        await new CustomBarcodeBuilder().isOptional(false).setSymbologies([Symbology.EAN13UPCA]).build('UPC')
      )
      .addImeiOneBarcode(
        await new ImeiOneBarcodeBuilder().isOptional(false).setSymbology(Symbology.Code128).build('IMEI1')
      )
      .addSerialNumberBarcode(
        await new SerialNumberBarcodeBuilder().isOptional(true).setSymbology(Symbology.Code128).build('Serial Number')
      )
      .adaptiveRecognitionMode(ctx.adaptiveRecognitionMode)
      .build('Smartphone Box')
    return await LabelCaptureSettings.fromLabelDefinitions([labelDefinition])
  }

  activate(mode: LabelCapture, view: DataCaptureView): Promise<Teardown> {
    return activateValidationFlow(mode, view, this.deps)
  }

  resume(mode: LabelCapture): Promise<void> {
    return mode.setEnabled(true)
  }
}
